/**
 * figCoords — 명세 좌표(수학) ↔ 그림 좌표(픽셀) 변환과, 손으로 옮길 수 있는 손잡이 목록.
 *
 * 서버가 그려 준 SVG 위에 손잡이를 얹어 끌 수 있게 하려면 두 좌표계를 이어야 한다.
 * 변환식은 **추측이 아니라 실측**이다 — 2026-08-28 서버가 낸 실제 도판 한 장을 역산해 얻었고,
 * 그 표본의 8개 좌표가 전부 오차 0.01px 안에서 맞는다(figCoords.test.js).
 *
 *   표본: width 310 · height 221 · x [-1,6] · y [-1,4]
 *         점 A(0,0) → (54.29, 168.4) · 점 B(5,3) → (255.71, 52.6)
 *         선분들: x=2 → 134.86 · x=4 → 215.43 · y=1 → 129.8 · y=2 → 91.2
 *
 * 여백 14 는 그 역산의 산물이라 서버가 조판을 바꾸면 어긋난다. 그래서 손잡이를 놓기 전에
 * **검산**한다(verifyTransform) — 명세의 점과 실제 그려진 자리가 어긋나면 끌기를 열지 않는다.
 * 어긋난 채 열면 사용자가 끈 자리와 그림이 따로 놀아, 고칠수록 나빠진다.
 */

/** 서버 도판의 사방 여백(px). 위 실측의 역산값이다. */
export const FIG_MARGIN = 14;

/** 손잡이가 「제자리」라고 볼 오차(px). 이보다 벌어지면 변환이 틀린 것으로 본다. */
export const FIG_MATCH_TOLERANCE = 2;

/**
 * 점으로 찍힌 동그라미의 반지름 상한(px) — 이보다 크면 도형(원·부채꼴)으로 본다.
 * 실측(dev 2026-09-06): 점은 `r="2.4"`. 여유를 두되 데이터 좌표로 그려지는 도형과는 겹치지 않는 값이다.
 */
const DOT_RADIUS_MAX = 6;

function range(v, fallbackLo, fallbackHi) {
  const lo = Array.isArray(v) && Number.isFinite(Number(v[0])) ? Number(v[0]) : fallbackLo;
  const hi = Array.isArray(v) && Number.isFinite(Number(v[1])) ? Number(v[1]) : fallbackHi;
  return [lo, hi];
}

/**
 * 변환기 하나. 못 만들면 null 을 돌려준다(범위가 없거나 붕괴한 경우).
 * @param spec 명세(x·y 범위를 읽는다)
 * @param size { width, height } — 렌더 응답이 준 실제 도판 크기
 */
export function figTransform(spec, size) {
  const W = Number(size?.width);
  const H = Number(size?.height);
  if (!Number.isFinite(W) || !Number.isFinite(H) || W <= 2 * FIG_MARGIN || H <= 2 * FIG_MARGIN) return null;
  const [x0, x1] = range(spec?.x, NaN, NaN);
  const [y0, y1] = range(spec?.y, NaN, NaN);
  if (![x0, x1, y0, y1].every(Number.isFinite)) return null;
  if (x1 - x0 === 0 || y1 - y0 === 0) return null;
  const sx = (W - 2 * FIG_MARGIN) / (x1 - x0);
  const sy = (H - 2 * FIG_MARGIN) / (y1 - y0);
  return {
    width: W,
    height: H,
    xRange: [x0, x1],
    yRange: [y0, y1],
    toPx: (x, y) => ({
      px: FIG_MARGIN + (Number(x) - x0) * sx,
      py: H - FIG_MARGIN - (Number(y) - y0) * sy,
    }),
    toData: (px, py) => ({
      x: x0 + (Number(px) - FIG_MARGIN) / sx,
      y: y0 + (H - FIG_MARGIN - Number(py)) / sy,
    }),
  };
}

/**
 * 변환이 실제 도판과 맞는지 검산한다.
 * 서버는 명세의 points 마다 <circle> 을 그린다. 그 자리와 우리가 계산한 자리를 대조한다.
 * @param svgText 렌더 응답의 svg_text
 * @returns { ok, checked, worst } — checked 가 0 이면 대조할 것이 없었다는 뜻(ok 는 true)
 */
export function verifyTransform(tr, spec, svgText) {
  /**
   * **화면에 점으로 찍힌 점만 센다**(2026-09-06 수정).
   *
   * 명세의 점에는 「동그라미를 찍을지」(`dot`)가 있고, 끄면 서버는 이름만 쓰고 동그라미를 그리지 않는다.
   * 그런데 이 검산은 점마다 **가장 가까운 동그라미까지의 거리**를 재므로, 안 찍힌 점은 제 동그라미가 없어
   * 남의 것과 재게 되고 그 거리가 그대로 「어긋남」이 됐다.
   *
   * 실측(dev 2026-09-06 · ocr_prob 685): 점 다섯 중 찍힌 둘은 오차 **0.0px** 인데, 안 찍힌 셋이
   * 125.3 · 125.3 · 151.9px 로 나와 그 최댓값 때문에 끌기가 막혔다. 687 은 같은 이유로 307px 였다.
   * 변환은 정확했고 검산만 틀렸다 — 그래서 세는 대상을 「그려진 점」으로 좁힌다.
   */
  const pts = (Array.isArray(spec?.points) ? spec.points : []).filter((p) => p?.dot !== false);
  if (!tr || pts.length === 0 || typeof svgText !== 'string') return { ok: true, checked: 0, worst: 0 };
  const circles = [];
  const re = /<circle[^>]*\bcx="([-\d.]+)"[^>]*\bcy="([-\d.]+)"/g;
  let m = re.exec(svgText);
  while (m) { circles.push([Number(m[1]), Number(m[2])]); m = re.exec(svgText); }
  if (circles.length === 0) return { ok: true, checked: 0, worst: 0 };
  let worst = 0;
  let checked = 0;
  pts.forEach((p) => {
    const want = tr.toPx(p?.x, p?.y);
    if (!Number.isFinite(want.px) || !Number.isFinite(want.py)) return;
    // 순서를 믿지 않는다 — 가장 가까운 원을 찾아 그 거리를 본다(circles[] 원소도 <circle> 이라 섞인다)
    let best = Infinity;
    circles.forEach(([cx, cy]) => {
      const d = Math.hypot(cx - want.px, cy - want.py);
      if (d < best) best = d;
    });
    checked += 1;
    if (best > worst) worst = best;
  });
  return { ok: worst <= FIG_MATCH_TOLERANCE, checked, worst };
}

export function snapTo(v, step) {
  const s = Number(step);
  if (!Number.isFinite(s) || s <= 0) return round6(v);
  return round6(Math.round(Number(v) / s) * s);
}

export function clamp(v, lo, hi) {
  return Math.min(Math.max(Number(v), Math.min(lo, hi)), Math.max(lo, hi));
}

// 부동소수 찌꺼기(0.30000000000000004)를 명세에 남기지 않는다 — 사람이 읽는 값이다
function round6(v) {
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n * 1e6) / 1e6 : n;
}

/**
 * 끌 수 있는 자리를 전부 모은다.
 * 곡선(curves)은 식이라 못 옮기고, 칠하기(shade)는 경계가 곡선이라 여기 들지 않는다.
 * vlines·hlines 는 한 축만 움직인다(kx 또는 ky 가 null).
 * 호·부채꼴은 **중심만** 끈다(반지름·각도는 숫자 칸). 각(angles)은 일부러 뺐다 — 아래 주석.
 */
export function collectHandles(spec) {
  const out = [];
  const arr = (k) => (Array.isArray(spec?.[k]) ? spec[k] : []);
  const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : null);

  arr('points').forEach((p, i) => {
    const x = num(p?.x); const y = num(p?.y);
    if (x != null && y != null) out.push({ id: `points.${i}`, key: 'points', i, kx: 'x', ky: 'y', x, y, name: p?.label ? `점 ${p.label}` : `점 ${i + 1}` });
  });
  arr('labels').forEach((p, i) => {
    const x = num(p?.x); const y = num(p?.y);
    if (x != null && y != null) out.push({ id: `labels.${i}`, key: 'labels', i, kx: 'x', ky: 'y', x, y, name: p?.t ? `글자 ${p.t}` : `글자 ${i + 1}` });
  });
  arr('segments').forEach((s, i) => {
    [['x1', 'y1', '시작'], ['x2', 'y2', '끝']].forEach(([kx, ky, tag], k) => {
      const x = num(s?.[kx]); const y = num(s?.[ky]);
      if (x != null && y != null) out.push({ id: `segments.${i}.${k}`, key: 'segments', i, kx, ky, x, y, name: `선분 ${i + 1} ${tag}` });
    });
  });
  arr('circles').forEach((c, i) => {
    const x = num(c?.cx); const y = num(c?.cy);
    if (x != null && y != null) out.push({ id: `circles.${i}`, key: 'circles', i, kx: 'cx', ky: 'cy', x, y, name: `원 ${i + 1} 중심` });
  });
  /**
   * 호(arcs)·부채꼴(sectors) 중심 — 원과 **같은 모양의 손잡이**다(`cx`/`cy` 두 키).
   * 그래서 applyHandle 의 일반 경로가 그대로 받는다. 새 갈래를 만들지 않는다.
   *
   * 반지름·각도(r·from·to)에는 손잡이를 두지 않는다 — 그 셋은 끌 방향이 하나로 정해지지 않아
   * (반지름은 방사, 각도는 회전) 점 하나를 끄는 이 층의 규칙에 맞지 않는다. 숫자 칸으로 고친다.
   *
   * **각(angles)에는 손잡이를 두지 않는다**(2026-08-31 판단, 빠뜨린 것이 아니다).
   * angles 의 at·from·to 는 거의 언제나 다각형 꼭짓점과 **같은 좌표**다
   * (계약 §9-1 예시 `at:[0,0] from:[4,0] to:[2,3]` = 삼각형 세 꼭짓점 그대로).
   * 손잡이를 얹으면 같은 자리에 둘이 겹쳐 어느 것을 잡았는지 알 수 없고, 위에 얹힌 쪽이
   * 이기므로 **삼각형 꼭짓점을 끌었는데 각 표시만 움직이는** 일이 난다. 겹침을 푸는 것은
   * 화면 정리 몫이라 그때 함께 본다.
   */
  ['arcs', 'sectors'].forEach((key) => {
    const name = key === 'arcs' ? '호' : '부채꼴';
    arr(key).forEach((a, i) => {
      const x = num(a?.cx); const y = num(a?.cy);
      if (x != null && y != null) out.push({ id: `${key}.${i}`, key, i, kx: 'cx', ky: 'cy', x, y, name: `${name} ${i + 1} 중심` });
    });
  });
  arr('polygons').forEach((g, i) => {
    (Array.isArray(g?.pts) ? g.pts : []).forEach((pt, j) => {
      const x = num(pt?.[0]); const y = num(pt?.[1]);
      if (x != null && y != null) out.push({ id: `polygons.${i}.${j}`, key: 'polygons', i, j, x, y, name: `다각형 ${i + 1} 꼭짓점 ${j + 1}` });
    });
  });
  const midY = (() => { const [a, b] = range(spec?.y, 0, 1); return (a + b) / 2; })();
  const midX = (() => { const [a, b] = range(spec?.x, 0, 1); return (a + b) / 2; })();
  arr('vlines').forEach((l, i) => {
    const x = num(l?.x);
    if (x != null) out.push({ id: `vlines.${i}`, key: 'vlines', i, kx: 'x', ky: null, x, y: midY, name: `세로선 ${i + 1}` });
  });
  arr('hlines').forEach((l, i) => {
    const y = num(l?.y);
    if (y != null) out.push({ id: `hlines.${i}`, key: 'hlines', i, kx: null, ky: 'y', x: midX, y, name: `가로선 ${i + 1}` });
  });
  return out;
}

/** 손잡이 하나를 옮긴 새 명세. 원본은 건드리지 않는다. */
export function applyHandle(spec, h, x, y) {
  if (!spec || !h) return spec;
  const next = { ...spec };
  const list = Array.isArray(next[h.key]) ? next[h.key].map((v) => (Array.isArray(v) ? [...v] : { ...v })) : [];
  if (h.key === 'polygons') {
    const g = list[h.i];
    if (!g) return spec;
    const pts = Array.isArray(g.pts) ? g.pts.map((p) => [...p]) : [];
    if (!pts[h.j]) return spec;
    pts[h.j] = [round6(x), round6(y)];
    list[h.i] = { ...g, pts };
  } else {
    const el = list[h.i];
    if (!el) return spec;
    const patched = { ...el };
    if (h.kx) patched[h.kx] = round6(x);
    if (h.ky) patched[h.ky] = round6(y);
    list[h.i] = patched;
  }
  next[h.key] = list;
  return next;
}

/**
 * SVG 의 <text> 를 { px, py, fontSize, text } 로 뽑는다. 속성 순서를 가정하지 않는다 —
 * 서버가 x·y·font-size·fill·text-anchor 를 어떤 차례로 쓰든 걸리게 한다.
 *
 * ⚠ **`font-size` 가 없을 때의 기본값이 13 이다**(계약 §9-4, 2026-08-31 「라벨 … 기본 13px」).
 * 2026-08-31 이전에는 11 이었는데 그것은 **우리가 짐작한 값**이었고, 계약이 실제 값을 적어 주며
 * 갈렸다. 이 값은 손잡이를 글자 한가운데로 올리는 데만 쓰이므로(아래 `py - fontSize * 0.35`)
 * 틀려도 조용히 2px 어긋날 뿐 아무것도 깨지지 않는다 — 그래서 더 조용히 낡는다.
 *
 * ⚠ **숫자 분수는 세로로 조판된다**(§9-4 「2 위·가로선·3 아래」). 그러면 라벨 하나가 `<text>`
 * 여럿으로 쪼개져 아래 `collectCurveLabelHandles` 의 1:1 대조가 안 짚인다 —
 * 손잡이가 **안 생기는 쪽으로** 안전하게 무너진다(엉뚱한 글자를 잡는 것보다 낫다).
 */
function parseSvgTexts(svgText) {
  const out = [];
  const re = /<text\b([^>]*)>([\s\S]*?)<\/text>/g;
  let m = re.exec(svgText);
  while (m) {
    const attrs = m[1];
    const px = Number((/\bx="([-\d.]+)"/.exec(attrs) || [])[1]);
    const py = Number((/\by="([-\d.]+)"/.exec(attrs) || [])[1]);
    const fs = Number((/\bfont-size="([\d.]+)"/.exec(attrs) || [])[1]);
    if (Number.isFinite(px) && Number.isFinite(py)) {
      out.push({ px, py, fontSize: Number.isFinite(fs) ? fs : 13, text: decodeXmlText(m[2]) });
    }
    m = re.exec(svgText);
  }
  return out;
}

// XML 이스케이프를 되돌린다 — 이름표에 < 나 & 가 들어가면 서버가 그렇게 쓴다.
// & 를 마지막에 푸는 것이 중요하다: 먼저 풀면 「&amp;lt;」가 「<」가 되어 원문이 뒤바뀐다.
function decodeXmlText(s) {
  return String(s)
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&')
    .trim();
}

/**
 * 곡선 이름표 손잡이 — 서버가 그린 도판의 <text> 자리를 읽어 만든다.
 *
 * 왜 SVG 를 읽는가: `labelX` 는 **가로 위치만** 정하고 세로는 곡선 위 f(labelX) 다.
 * 그 값을 알려면 식을 풀어야 하는데 식 평가기는 서버에만 있다(POST /figspec/render).
 * 대신 서버가 이미 그려 준 도판에서 그 글자가 놓인 자리를 그대로 읽는다 —
 * verifyTransform 이 <circle> 을 읽어 변환을 검산하는 것과 같은 수법이다.
 *
 * 세로로는 움직이지 않는다(ky:null). 계약에 labelY 가 없고 세로는 곡선이 정하기 때문이다.
 * 같은 규칙을 vlines(가로만)·hlines(세로만)가 이미 쓴다.
 *
 * 이름표 글자와 <text> 가 **1:1 로 짚이지 않으면**(없거나 둘 이상) 손잡이를 주지 않는다 —
 * 엉뚱한 이름표를 잡게 하느니 안 잡히는 편이 낫다.
 */
export function collectCurveLabelHandles(spec, svgText, tr) {
  if (!tr || typeof svgText !== 'string') return [];
  const curves = Array.isArray(spec?.curves) ? spec.curves : [];
  if (curves.length === 0) return [];
  const texts = parseSvgTexts(svgText);
  if (texts.length === 0) return [];
  const out = [];
  curves.forEach((c, i) => {
    const label = typeof c?.label === 'string' ? c.label.trim() : '';
    if (label === '') return;
    const hit = texts.filter((t) => t.text === label);
    if (hit.length !== 1) return; // 못 찾았거나 여러 개다 — 짚이지 않으면 잡히지 않게 둔다
    const { px, py, fontSize } = hit[0];
    // <text> 의 y 는 베이스라인이다. 글자 한가운데로 손잡이를 올려 무엇을 잡는지 눈에 맞춘다.
    const d = tr.toData(px, py - fontSize * 0.35);
    if (!Number.isFinite(d.x) || !Number.isFinite(d.y)) return;
    out.push({
      id: `curves.${i}.label`,
      key: 'curves',
      i,
      kx: 'labelX',
      ky: null,   // curves 에 labelY 가 없다
      freeY: true, // 그래도 세로로 끌 수는 있다 — applyCurveLabelHandle 이 labels[] 로 떼어낸다
      x: d.x,
      y: d.y,
      name: `이름 ${label}`,
    });
  });
  return out;
}

/**
 * 곡선 이름표를 옮긴다 — 가로는 제자리에서, 세로는 떼어내서.
 *
 * 가로만 옮기면 `curves[].labelX` 하나로 끝난다. 그런데 세로는 적을 곳이 없다 —
 * 계약의 curves 는 `{expr, label, labelX}` 가 전부이고 **labelY 가 없다**
 * (BE-ref-figspec-methodology.md §41). 세로를 못 옮기면 「위치만 바꾸는 기능」이 반쪽이다.
 *
 * 그래서 세로가 바뀌는 순간 **이름표를 자유 글자(labels[])로 떼어낸다.** 계약 안에서 글자를
 * 임의의 자리에 두는 유일한 길이다. 떼어낸 뒤에는 labels 손잡이가 이어받아 가로·세로 모두 자유롭다.
 *
 * 대가: 곡선과의 연결이 끊어진다 — 식을 바꿔도 이름표가 따라가지 않는다. 그것이 「자리를 내가
 * 정한다」의 값이고, 되돌리려면 숫자 폼에서 그 글자를 지우고 곡선의 이름을 다시 채우면 된다.
 * 가로만 움직였을 때 떼어내지 않는 이유가 그것이다 — 멀쩡한 연결을 가로 이동만으로 끊지 않는다.
 */
export function applyCurveLabelHandle(spec, h, x, y) {
  if (!spec || !h) return spec;
  const curves = Array.isArray(spec.curves) ? spec.curves.map((c) => ({ ...c })) : [];
  const c = curves[h.i];
  if (!c) return spec;
  if (round6(y) === round6(h.y)) {
    curves[h.i] = { ...c, labelX: round6(x) };
    return { ...spec, curves };
  }
  const t = typeof c.label === 'string' ? c.label.trim() : '';
  if (t === '') return spec; // 뗄 이름표가 없다
  delete c.label;
  delete c.labelX; // 이름표가 곡선을 떠났으니 그 가로 위치도 뜻이 없다
  const labels = Array.isArray(spec.labels) ? spec.labels.map((l) => ({ ...l })) : [];
  labels.push({ x: round6(x), y: round6(y), t });
  return { ...spec, curves, labels };
}

/**
 * 도판에 **점으로 찍힌 동그라미**만 모은다 — 원·부채꼴 같은 도형도 `<circle>` 로 그려지므로
 * 반지름으로 가른다(점은 2~4px 안팎, 도형은 데이터 좌표라 대개 훨씬 크다).
 * 문서 순서 그대로 돌려준다 — 서버가 명세의 점 차례로 그리므로 짝짓기의 기준이 된다.
 */
function collectDotCircles(svgText) {
  const out = [];
  if (typeof svgText !== 'string') return out;
  const re = /<circle[^>]*\bcx="([-\d.]+)"[^>]*\bcy="([-\d.]+)"[^>]*\br="([-\d.]+)"/g;
  let m = re.exec(svgText);
  while (m) {
    const r = Number(m[3]);
    if (Number.isFinite(r) && r <= DOT_RADIUS_MAX) out.push([Number(m[1]), Number(m[2])]);
    m = re.exec(svgText);
  }
  return out;
}

/**
 * **여백을 가정하지 않고, 그려진 그림에서 변환을 되맞춘다**(2026-09-06).
 *
 * `figTransform` 의 여백 14 는 2026-08-28 도판 한 장을 역산한 값이다. 서버가 그 뒤로 조판을 바꿔
 * (눈금 글자가 잘리지 않게 좌우 여백을 넓히는 일이 있다 — 백엔드 2026-09-04) 그 가정이 틀리면
 * 종전에는 끌기를 통째로 닫았다. 그런데 **틀린 것은 여백뿐이고 비례는 그대로**다:
 * 실측(dev · ocr_prob 682)에서 되맞춘 여백은 좌 23.1 · 우 44.2 · 위아래 14 였고, 그 뒤 잔차는 0.01px 였다.
 *
 * 그래서 닫는 대신 **명세의 점 ↔ 도판의 동그라미**를 문서 순서로 짝지어 최소제곱으로 푼다
 * (`px = a + x·sx`, `py = b − y·sy`). 되맞춘 변환은 서버 조판과 정확히 같으므로, 끌면 그 자리에 간다.
 *
 * 못 푸는 경우에는 null 을 돌려준다 — 그때는 종전대로 닫는다. 지어낸 변환으로 열면 끌수록 나빠진다.
 *   · 짝이 안 맞는다(찍힌 점 수 ≠ 동그라미 수) · 점이 둘 미만
 *   · x 가 모두 같거나 y 가 모두 같다(한 축의 배율을 풀 수 없다)
 *   · 잔차가 허용 오차를 넘는다 · 배율이 0 이하이거나 유한하지 않다
 */
export function fitTransform(spec, size, svgText) {
  const W = Number(size?.width);
  const H = Number(size?.height);
  if (!Number.isFinite(W) || !Number.isFinite(H) || W <= 0 || H <= 0) return null;
  const pts = (Array.isArray(spec?.points) ? spec.points : [])
    .filter((p) => p?.dot !== false && Number.isFinite(Number(p?.x)) && Number.isFinite(Number(p?.y)));
  const cs = collectDotCircles(svgText);
  if (pts.length < 2 || pts.length !== cs.length) return null;

  const n = pts.length;
  const xs = pts.map((p) => Number(p.x));
  const ys = pts.map((p) => Number(p.y));
  const mean = (a) => a.reduce((t, v) => t + v, 0) / n;
  const mx = mean(xs);
  const my = mean(ys);
  const mpx = mean(cs.map((c) => c[0]));
  const mpy = mean(cs.map((c) => c[1]));
  const varX = xs.reduce((t, v) => t + (v - mx) ** 2, 0);
  const varY = ys.reduce((t, v) => t + (v - my) ** 2, 0);
  if (varX === 0 || varY === 0) return null; // 한 줄로 늘어선 점 — 그 축의 배율을 풀 수 없다
  const sx = xs.reduce((t, v, i) => t + (v - mx) * (cs[i][0] - mpx), 0) / varX;
  // 화면 y 는 아래로 자라고 수학 y 는 위로 자란다 — 부호를 뒤집어 「위로 자라는 배율」로 둔다.
  const sy = -ys.reduce((t, v, i) => t + (v - my) * (cs[i][1] - mpy), 0) / varY;
  if (!Number.isFinite(sx) || !Number.isFinite(sy) || sx <= 0 || sy <= 0) return null;
  const a = mpx - sx * mx;
  const b = mpy + sy * my;

  let worst = 0;
  for (let i = 0; i < n; i += 1) {
    const d = Math.hypot(a + sx * xs[i] - cs[i][0], b - sy * ys[i] - cs[i][1]);
    if (d > worst) worst = d;
  }
  if (worst > FIG_MATCH_TOLERANCE) return null;

  // 범위는 명세 것을 그대로 쓴다 — 끌기가 그 안으로 가두는 값이라 조판과 무관하다.
  const [x0, x1] = range(spec?.x, NaN, NaN);
  const [y0, y1] = range(spec?.y, NaN, NaN);
  if (![x0, x1, y0, y1].every(Number.isFinite) || x1 - x0 === 0 || y1 - y0 === 0) return null;
  return {
    width: W,
    height: H,
    xRange: [x0, x1],
    yRange: [y0, y1],
    fitted: true,
    toPx: (x, y) => ({ px: a + sx * Number(x), py: b - sy * Number(y) }),
    toData: (px, py) => ({ x: (Number(px) - a) / sx, y: (b - Number(py)) / sy }),
  };
}

/**
 * **손으로 옮기기가 열리는가** — 열리지 않으면 그 까닭까지 한 번에 정한다(2026-09-06).
 *
 * 종전에는 이 판정이 FigDragLayer 안에 있었고, 열리지 않는 까닭을 **그림 위에 겹쳐** 띄웠다.
 * 그 자리가 하필 그림을 가려(도판 아래쪽), 오너가 「머리줄로 올려 달라」고 했다. 머리줄은 이 층이
 * 아니라 그림 창(FigureModal)이 그리므로, 판정을 여기로 끌어내 **한 곳에서** 정하고 두 자리가
 * 같은 값을 읽게 한다 — 갈라 두면 창의 머리줄과 손잡이의 유무가 어긋난다.
 *
 * 세 갈래로 막힌다(막는 차례가 곧 아래 검사 순서다) —
 *   · `no-range`   가로·세로 범위가 없다 → 픽셀↔수학 좌표 변환 자체가 서지 않는다.
 *   · `mismatch`   변환이 실제 도판과 어긋난다(서버가 조판을 바꿨다) → 끌수록 그림이 나빠진다.
 *   · `no-handles` 끌 자리가 하나도 없다(곡선만 있는 그림 등).
 *
 * @returns { reason, worst, tr, handles } — `reason` 이 null 이면 열린다. `tr`·`handles` 는 그때 쓰는 값이다.
 */
export function figDragState(spec, size, svgText) {
  const guess = figTransform(spec, size);
  if (!guess) return { reason: 'no-range', worst: 0, tr: null, handles: [] };
  const check = verifyTransform(guess, spec, svgText);
  /**
   * 여백 가정이 틀렸을 때 **닫기 전에 되맞춰 본다**(2026-09-06). 되맞춘 변환은 그려진 그림에서 뽑은
   * 것이라 조판이 바뀌어도 정확하다 — 그것마저 못 풀 때만 닫는다(fitTransform 머리주석).
   */
  const tr = check.ok ? guess : fitTransform(spec, size, svgText);
  if (!tr) return { reason: 'mismatch', worst: check.worst, tr: guess, handles: [] };
  // 곡선 이름표는 명세만으로 자리를 알 수 없어 도판에서 읽는다 — 변환이 선 뒤에만 뜻이 있다.
  const handles = [...collectHandles(spec), ...collectCurveLabelHandles(spec, svgText, tr)];
  if (handles.length === 0) return { reason: 'no-handles', worst: 0, tr, handles };
  return { reason: null, worst: 0, tr, handles };
}
